import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { creators } from '../api';
import { B } from '../constants';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import { trackActivity } from '../lib/activityEvents';

export default function CreatorProfileScreen({ goScreen, user }) {
  const { username } = useParams();
  const trackedUsernameRef = useRef(null);
  const [creator, setCreator] = useState(null);
  const [reels, setReels] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('reels'); // 'reels' or 'trips'

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await creators.getProfile(username);
      setCreator(data.creator);
      if (trackedUsernameRef.current !== username) {
        trackedUsernameRef.current = username;
        trackActivity('content', 'creator_profile_viewed', { username });
      }
      setReels(data.reels || []);
      setTrips(data.trips || []);
    } catch (err) {
      setError(err.message || 'Failed to load creator profile');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return <LoadingState variant="spinner" message="Loading creator profile..." />;
  }

  if (error || !creator) {
    return (
      <EmptyState
        icon="👤"
        title="Creator not found"
        description="This creator profile doesn't exist or has been removed."
        action={{ label: 'Go Back', onClick: () => goScreen('home') }}
        variant="withIllustration"
      />
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
      {/* Creator Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 30 }}>
        <img
          src={creator.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.name)}`}
          alt={creator.name}
          style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
        />
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 28 }}>{creator.name}</h1>
          <p style={{ margin: '0 0 8px 0', color: B.muted }}>@{creator.username}</p>
          {creator.creatorBio && (
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.5 }}>{creator.creatorBio}</p>
          )}
          <div style={{ display: 'flex', gap: 20, fontSize: 14, color: B.muted }}>
            <span><strong style={{ color: '#000' }}>{creator.followerCount || 0}</strong> followers</span>
            <span><strong style={{ color: '#000' }}>{creator.tripCount || 0}</strong> trips</span>
            <span><strong style={{ color: '#000' }}>{creator.reelCount || 0}</strong> reels</span>
          </div>
        </div>
      </div>

      {/* Tip Jar */}
      {creator.tipJarStripeLink && (
        <a
          href={creator.tipJarStripeLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '10px 20px',
            backgroundColor: B.amber,
            color: '#000',
            textDecoration: 'none',
            borderRadius: 8,
            fontWeight: 600,
            marginBottom: 30,
          }}
        >
          💰 Tip Jar
        </a>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 20, borderBottom: '2px solid #e5e7eb', marginBottom: 20 }}>
        <button
          onClick={() => setActiveTab('reels')}
          style={{
            padding: '10px 0',
            border: 'none',
            borderBottom: activeTab === 'reels' ? `2px solid ${B.sky}` : '2px solid transparent',
            backgroundColor: 'transparent',
            fontWeight: activeTab === 'reels' ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          Reels ({reels.length})
        </button>
        <button
          onClick={() => setActiveTab('trips')}
          style={{
            padding: '10px 0',
            border: 'none',
            borderBottom: activeTab === 'trips' ? `2px solid ${B.sky}` : '2px solid transparent',
            backgroundColor: 'transparent',
            fontWeight: activeTab === 'trips' ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          Trips ({trips.length})
        </button>
      </div>

      {/* Reels Grid */}
      {activeTab === 'reels' && (
        <>
          {reels.length === 0 ? (
            <p style={{ textAlign: 'center', color: B.muted, padding: 40 }}>No reels yet</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
              {reels.map((reel) => (
                <Link
                  key={reel.id}
                  to={`/reels/${reel.id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <img
                      src={reel.thumbnailUrl || 'https://via.placeholder.com/200x300'}
                      alt={reel.title}
                      style={{ width: '100%', height: 200, objectFit: 'cover' }}
                    />
                    <div style={{ padding: 12 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>{reel.title || 'Untitled'}</h4>
                      <p style={{ margin: 0, fontSize: 12, color: B.muted }}>{reel.destination}</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: 12, color: B.muted }}>{reel.views || 0} views</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {/* Trips Grid */}
      {activeTab === 'trips' && (
        <>
          {trips.length === 0 ? (
            <p style={{ textAlign: 'center', color: B.muted, padding: 40 }}>No public trips yet</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 20 }}>
              {trips.map((trip) => (
                <Link
                  key={trip.id}
                  to={`/trips/${trip.id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{ borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <img
                      src={trip.coverImage || 'https://via.placeholder.com/300x200'}
                      alt={trip.title}
                      style={{ width: '100%', height: 180, objectFit: 'cover' }}
                    />
                    <div style={{ padding: 16 }}>
                      <h4 style={{ margin: '0 0 8px 0' }}>{trip.title}</h4>
                      <p style={{ margin: '0 0 4px 0', fontSize: 14, color: B.muted }}>{trip.destination}</p>
                      <p style={{ margin: 0, fontSize: 12, color: B.muted }}>
                        {trip._count?.days || 0} days • {trip._count?.experiences || 0} experiences
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
